/**
 * ownOrganisations (#3371) — the pure half of the "Eigene Organisationen"
 * settings section: which organisations belong to the farm group itself.
 *
 * **No new endpoint, no new backend half.** The issue title reads *"no
 * production writer for the own-group flag exists today"*, and that is true of
 * the FLAG, not of the MECHANISM. The generic entity-attribute write path is
 * shipped and already driven from the SPA by ``GutschriftCheckPage``:
 *
 *     twinService.editEntity('organization', id, {internal: true})
 *       -> PUT /v1/twin/entities/organization/{id}          (202 Accepted)
 *       -> TwinDataService.push_entity_edit  (Redis ZADD + RabbitMQ)
 *       -> worker link_to_entities  source == "user_edit"
 *       -> entity_store.update_entity + one ``user_edit`` fact
 *
 * Three mechanics of that path decide everything in this module:
 *
 * 1. **The read cannot go through the GET filter path.**
 *    ``useEntities('organization', {internal: 'true'})`` stringifies the value
 *    into a query param, which UDB compiles to JSONB containment
 *    ``attributes @> '{"internal":"true"}'`` — a predicate that cannot match a
 *    JSONB boolean ``true``. It returns an EMPTY list with NO error. Only the
 *    ``POST .../query`` ``attr_in`` shape works, because it compiles to
 *    ``(attributes ->> 'internal') = ANY(ARRAY['true'])`` and ``->>`` renders a
 *    JSONB boolean as the text ``'true'``. Hence
 *    :func:`ownOrganisationsQueryBody`, and hence the request-shape assertion in
 *    the sibling test file — nothing else would catch a regression here, because
 *    the failure mode is a silent zero-row list on a surface whose empty state is
 *    a legitimate answer.
 *
 * 2. **Attributes MERGE; they are never deleted.** ``update_entity`` on the
 *    user-edit path (no ``owned_attribute_keys``) is ``{**existing, **updates}``,
 *    so "remove from the list" has to WRITE a falsy value.
 *    :func:`ownOrganisationFieldUpdate` writes ``internal: null``, matching both
 *    the ontology's declared *"Absent/null = external"* and the only other
 *    production writer (``wibill_backfill.py``'s ``True if internal else None``).
 *
 * 3. **Never write ``is_farm_self``.** It is undeclared in ``config/ontology/``
 *    and every attributes model is built with ``ConfigDict(extra="forbid")``, so
 *    a write raises ValidationError — which the worker classifies as a
 *    DETERMINISTIC twin failure: dead-lettered, ACKed, never retried. The edit
 *    would vanish leaving only a DLQ row. This module writes ``internal`` and
 *    nothing else. (The ``is_farm_self`` -> ``internal`` code convergence is
 *    #3370's, deliberately out of scope here.)
 *
 * The view half (``OwnOrganisationsSection.tsx`` /
 * ``OwnOrganisationsManager.tsx``) owns the mounting discipline and the
 * optimistic state; everything decidable without React lives here.
 */
import {
    organizationOptions,
    type CounterpartyOption,
} from '../apps/liquiditaetsplanung/counterparty';

/** The twin entity type the whole surface reads and writes. */
export const OWN_ORG_ENTITY_TYPE = 'organization';

/**
 * The declared ontology attribute (``config/ontology/organization.yaml``,
 * *Intern*, ``type: bool``). The ONLY attribute this surface writes.
 */
export const OWN_ORG_ATTRIBUTE = 'internal';

/**
 * Row cap for the own-group read. A farm group is a handful of legal entities,
 * not a corpus — the largest population measured in production is 12 of 757
 * organisations — so one generous page is the whole list and the section needs
 * no paginator. Should a tenant ever exceed this the list truncates rather than
 * misreports, and the picker still reaches every organisation.
 */
export const OWN_ORG_PAGE_LIMIT = 200;

/** Hits shown at once in the add-picker. Same reasoning as ``CounterpartyPicker``:
 *  this is a "find the one you mean" field, not a browser. */
export const OWN_ORG_PICKER_PAGE_SIZE = 8;

/**
 * Shortest term that earns a server read. ``useEntities``' ``q`` is a
 * case-insensitive substring scan over ``entity_id`` + ``attributes::text``, so
 * one character matches most of the pool: a page of noise for no signal.
 */
export const OWN_ORG_MIN_QUERY_LENGTH = 2;

/**
 * One organisation as this surface renders it. Structurally identical to the
 * Gegenpartei picker's option — deliberately the same shape, because it is the
 * same projection of the same entity type.
 */
export type OwnOrganisationOption = CounterpartyOption;

/**
 * The POST ``.../query`` body that reads the own-group list.
 *
 * See mechanic 1 in the module header: the ``attr_in`` shape is not a
 * preference, it is the only shape that can match a JSONB boolean. It also
 * excludes ``false``, ``null`` and absent correctly, which is what makes the
 * remove path (mechanic 2) legible to the same read.
 */
export function ownOrganisationsQueryBody(limit: number = OWN_ORG_PAGE_LIMIT): {
    attr_in: Record<string, string[]>;
    limit: number;
} {
    return { attr_in: { [OWN_ORG_ATTRIBUTE]: ['true'] }, limit };
}

/**
 * The ``field_updates`` payload for adding (``true``) or removing (``null``).
 *
 * ``null`` rather than ``false`` on the clear: both read as external through the
 * worker's ``_flag`` (``_FALSEY`` contains ``""``, which is what
 * ``str(None or "")`` and ``str(False or "")`` both produce), but ``null`` is what
 * the ontology declares and what the existing writer emits, so a hand-read of the
 * attributes column stays uniform.
 */
export function ownOrganisationFieldUpdate(flagged: boolean): Record<string, unknown> {
    return { [OWN_ORG_ATTRIBUTE]: flagged ? true : null };
}

/** The trimmed term to search for, or ``null`` when it is not worth a read. */
export function ownOrganisationSearchTerm(raw: string | undefined): string | null {
    const term = (raw ?? '').trim();
    return term.length >= OWN_ORG_MIN_QUERY_LENGTH ? term : null;
}

/**
 * Project one ``organization`` entity page into options.
 *
 * Delegates to the Gegenpartei picker's projection rather than restating it: the
 * id-resolution chain (``primary_key`` -> ``entity_id`` -> ``id``) is the part
 * with real, already-debugged logic behind it — UDB-backed organisation rows
 * surface only ``entity_id``, so a primary-key-only read yields options that
 * cannot be written to. Two implementations of that chain is exactly the
 * divergence that would produce a silent bug on one surface and not the other.
 */
export function ownOrganisationOptions(
    rows: Record<string, unknown>[] | undefined,
    primaryKey?: string | null,
): OwnOrganisationOption[] {
    return organizationOptions(rows, primaryKey);
}

/**
 * The add-picker's hit list, minus the organisations already on the list.
 * Offering an option whose only effect is to rewrite ``internal: true`` over
 * itself would read as a broken button.
 */
export function pickableOrganisations(
    hits: OwnOrganisationOption[],
    excludedIds: ReadonlySet<string>,
): OwnOrganisationOption[] {
    return hits.filter((hit) => !excludedIds.has(hit.id));
}

/**
 * The optimistic overlay: organisations flagged (``added``) or unflagged
 * (``removed``) locally but not yet visible in a server read.
 */
export interface OwnOrgOverlay {
    added: ReadonlyMap<string, OwnOrganisationOption>;
    removed: ReadonlySet<string>;
}

/** An overlay holding nothing. A module constant so a remount cannot mint a new
 *  identity and re-render every consumer for no reason. */
export const EMPTY_OWN_ORG_OVERLAY: OwnOrgOverlay = { added: new Map(), removed: new Set() };

/** One id's membership in the overlay — everything needed to undo exactly it. */
export interface OwnOrgOverlayEntry {
    added?: OwnOrganisationOption;
    removed: boolean;
}

/** Snapshot one id's membership, before a mutation overwrites it. */
export function overlayEntry(overlay: OwnOrgOverlay, id: string): OwnOrgOverlayEntry {
    const added = overlay.added.get(id);
    return { ...(added ? { added } : {}), removed: overlay.removed.has(id) };
}

/** Record an optimistic add (``flagged``) or remove for one organisation. */
export function applyOverlay(
    overlay: OwnOrgOverlay,
    option: OwnOrganisationOption,
    flagged: boolean,
): OwnOrgOverlay {
    const added = new Map(overlay.added);
    const removed = new Set(overlay.removed);
    if (flagged) {
        added.set(option.id, option);
        removed.delete(option.id);
    } else {
        removed.add(option.id);
        added.delete(option.id);
    }
    return { added, removed };
}

/**
 * Undo ONE id's optimistic change, restoring the membership `prior` captured
 * before it.
 *
 * **Per id, never a whole-overlay snapshot.** Restoring a snapshot taken at
 * mutate time looks equivalent and is not: two writes overlap routinely (both
 * buttons stay live while another row is in flight, and "remove these three" is
 * an ordinary gesture), and the second write's snapshot predates the first's
 * effect — so rolling the first one back by assignment would silently discard a
 * DIFFERENT organisation's *successful* change. On this surface that means a tile
 * the farmer removed reappears, or one they added disappears, with nothing to
 * correct it until the worker drains. Touching only the failed id cannot do that.
 */
export function revertOverlay(
    overlay: OwnOrgOverlay,
    id: string,
    prior: OwnOrgOverlayEntry,
): OwnOrgOverlay {
    const added = new Map(overlay.added);
    const removed = new Set(overlay.removed);
    if (prior.added) added.set(id, prior.added);
    else added.delete(id);
    if (prior.removed) removed.add(id);
    else removed.delete(id);
    return { added, removed };
}

/**
 * Fold the server's list together with the optimistic overlay.
 *
 * **Why the overlay outlives the invalidation.** The write is 202-async: the
 * mutation's ``onSuccess`` fires as soon as the edit is buffered, long before the
 * worker drains Redis and applies it, so the refetch that invalidation triggers
 * re-reads the PRE-edit list. Clearing an override on success is therefore
 * precisely what makes a just-added tile visibly vanish a beat after appearing.
 * It is reverted on ERROR only; once the worker catches up this fold is
 * idempotent, because a server row and its override collapse on id.
 *
 * A re-add deliberately beats a pending remove of the same organisation, so the
 * last gesture the farmer made is the one on screen.
 *
 * Sorted by label so the list does not reorder itself between two reads of a
 * server that promises no ordering.
 */
export function resolveOwnOrganisations(
    serverOptions: OwnOrganisationOption[],
    overlay: OwnOrgOverlay,
): OwnOrganisationOption[] {
    const out: OwnOrganisationOption[] = [];
    const seen = new Set<string>();
    for (const option of serverOptions) {
        if (seen.has(option.id)) continue;
        if (overlay.removed.has(option.id) && !overlay.added.has(option.id)) continue;
        seen.add(option.id);
        out.push(option);
    }
    for (const [id, option] of overlay.added) {
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(option);
    }
    return out.sort((a, b) => a.label.localeCompare(b.label, 'de'));
}
